import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { changeOwnPassword, type AuthUser } from "../services/authService";
import { APP_FIELD_SX, APP_UI } from "../ui/appUi";
import { classifyRequestFailure } from "../utils/fetchWithTimeout";
import { AUTH_VALIDATION, boundedTextError } from "../utils/apiValidation";

export default function PasswordChangePage({ user, onChanged }: { user: AuthUser; onChanged: (user: AuthUser) => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    const currentPasswordError = boundedTextError("Temporäres Passwort", currentPassword, AUTH_VALIDATION.password_input, false);
    if (currentPasswordError) { setError(currentPasswordError); return; }
    const newPasswordError = boundedTextError("Das neue Passwort", newPassword, AUTH_VALIDATION.new_password, false);
    if (newPasswordError) { setError(newPasswordError); return; }
    if (newPassword !== repeatPassword) { setError("Die beiden neuen Passwörter stimmen nicht überein."); return; }
    setBusy(true); setError(null);
    try { onChanged(await changeOwnPassword(currentPassword, newPassword)); }
    catch (unknownError) {
      const message = unknownError instanceof Error ? unknownError.message : "Passwort konnte nicht geändert werden.";
      const failureKind = classifyRequestFailure(unknownError);
      setError(failureKind === "timeout"
        ? message
        : (!navigator.onLine || failureKind === "network")
          ? "Passwort konnte nicht geändert werden. Für eine Passwortänderung ist eine Verbindung zum Server erforderlich."
          : message);
    }
    finally { setBusy(false); }
  }

  const passwordField = (label: string, value: string, onChange: (value: string) => void, autoComplete: string) => (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      type={show ? "text" : "password"}
      autoComplete={autoComplete}
      fullWidth
      disabled={busy}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label={show ? "Passwort ausblenden" : "Passwort anzeigen"} title={show ? "Passwort ausblenden" : "Passwort anzeigen"} onClick={() => setShow((current) => !current)} sx={{ color: "rgba(255,255,255,.82)" }}>
                {show ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      sx={APP_FIELD_SX}
    />
  );

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "transparent", color: APP_UI.text, display: "grid", placeItems: "center", px: 2 }}>
      <Stack spacing={1.3} sx={{ width: "100%", maxWidth: 430 }}>
        <Box sx={{ textAlign: "center", mb: 0.8 }}>
          <LockResetRoundedIcon sx={{ color: APP_UI.warning, fontSize: 48 }} />
          <Typography variant="h5" sx={{ fontWeight: 950, mt: 0.5 }}>Passwort ändern</Typography>
          <Typography variant="body2" sx={{ color: APP_UI.textMuted, mt: 0.5 }}>
            {user.username}, du hast dich mit einem temporären Passwort angemeldet. Vor der Nutzung der App muss ein eigenes Passwort gesetzt werden.
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ borderRadius: 2.7 }}>{error}</Alert>}
        {passwordField("Temporäres Passwort", currentPassword, setCurrentPassword, "current-password")}
        {passwordField("Neues Passwort", newPassword, setNewPassword, "new-password")}
        {passwordField("Neues Passwort wiederholen", repeatPassword, setRepeatPassword, "new-password")}
        <Button variant="contained" disabled={busy || !currentPassword || !newPassword || !repeatPassword} onClick={() => { void submit(); }} sx={{ minHeight: 50, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
          {busy ? "Passwort wird geändert …" : "Passwort speichern"}
        </Button>
      </Stack>
    </Box>
  );
}
